import type {
  CopilotProposal,
  CopilotProposalId,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import {
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const copilotProposalApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * The proposals one chat thread has produced, for the index the chat pins
     * above its composer. A summary per proposal — the editor fetches the
     * aggregates for the one proposal the user opens.
     */
    listThreadProposals: builder.query<
      readonly CopilotProposalSummary[],
      { threadId: string }
    >({
      query: ({ threadId }) => ({
        method: "GET",
        url: `/copilot/threads/${encodeURIComponent(threadId)}/proposals`,
      }),
      transformResponse: (response: {
        data: { proposals: readonly CopilotProposalSummary[] };
      }) => response.data.proposals,
      providesTags: (result, _error, { threadId }) =>
        getTagsProvidedByList(TagType.CopilotProposal, result, {
          filteredBy: [threadId],
        }),
    }),

    /**
     * One live proposal as stored, for the editor opened from its chat card
     * or row (see `useDeepLinkedProposal`). Only a pending row is found.
     */
    getPendingProposal: builder.query<CopilotProposal, CopilotProposalId>({
      query: (proposalId) => ({
        method: "GET",
        url: `/copilot/proposals/${encodeURIComponent(proposalId)}`,
      }),
      transformResponse: (response: { data: { proposal: CopilotProposal } }) =>
        response.data.proposal,
      providesTags: (_result, _error, proposalId) =>
        getTagsProvidedByEntity(TagType.CopilotProposal, proposalId),
      // Drop the entry as soon as nothing subscribes to it, which is once the
      // deep link strips its param, so a failed lookup isn't replayed as "no
      // longer available" on the next visit.
      keepUnusedDataFor: 0,
    }),

    /**
     * Resolve one live proposal — fired when the user applies or discards a
     * suggestion. The row is kept; only its status transitions.
     */
    resolveProposal: builder.mutation<
      void,
      {
        proposalId: CopilotProposalId;
        status: "applied" | "discarded";
      }
    >({
      query: ({ proposalId, status }) => ({
        method: "POST",
        url: `/copilot/proposals/${encodeURIComponent(proposalId)}/resolve`,
        body: { status },
      }),
      // The chat's proposal index lists this proposal, and an applied or
      // discarded one has no business staying on it.
      invalidatesTags: (_result, _error, { proposalId }) =>
        getTagsInvalidatedByUpdate(TagType.CopilotProposal, proposalId),
    }),
  }),
});

export const {
  useGetPendingProposalQuery,
  useListThreadProposalsQuery,
  useLazyListThreadProposalsQuery,
  useResolveProposalMutation,
} = copilotProposalApi;
